/**
 * 68.25 (lane D; decision 156, the owner: "if there's a stalemate if a bot could realize there's an unused apc nearby
 * with a 50 cal and bring it up on a ridgeline to improve group odds in stalemate"): where a vehicle's .50 is brought up
 * to cover an objective — the play's support role on wheels. Pure, never stored: the commander's stalemate move reads it
 * (ai/squadOrders.ts), and lane B's technical loop can read the same pick.
 *
 * **The band.** Past the rifle's effective range (core/support.ts `rifleEffectivePeople`, 25 people) and within `reach`
 * of the .50's own (its spread cone as wide as a person: `hmgEffectivePeople`, 31 people): the gun outranges the rifles it
 * keeps down, and is still sure of a body.
 *
 * **The choice**, nearest the vehicle (`from`) in each kind, the first kind found winning:
 * 1. **crest** — a crest of the relief grid (`terrainSpotsNear`) in the band whose gunner's eye (the cupola's, `eyePeople`
 *    over the ground) sees the objective's: the owner's ridgeline;
 * 2. **open** — the band's middle on the line from the objective toward the vehicle.
 */
import type { GameState, StructureState, Vec3 } from '../types';
import { MOUNTED_GUNS, VEHICLE_MODEL_SIZE, LOS_EYE_HEIGHT } from '../constants';
import { vecAdd, vecDot, vecNormalize, vecScale, vecSub, vecLength } from '../math';
import { dacos, dcos, dsin } from './dmath';
import { terrainSpotsNear } from './coverMap';
import { eyePointAt } from './los';
import { personLineOfSight } from './fineTerrain';
import { segmentCovered } from './bullets';
import { personSetOf, PERSON_BODY_HEIGHT } from './scaleProfile';
import { rifleEffectivePeople, SUPPORT_POSITION } from './support';
import { hatchStandPeople } from './drive';

/** The dials: the band's far edge as a share of the .50's effective range, and the gunner's eye over the ground (people: the cupola's rim, 1.43, and a head over it). */
export const GUN_SUPPORT = { reach: 1.15, eyePeople: 1.9 };

/** The .50's effective range in people: where its spread cone is as wide as a person. */
export function hmgEffectivePeople(): number {
  return SUPPORT_POSITION.bodyHalfWidthPeople / MOUNTED_GUNS.hmg.spread;
}

export interface GunSupportPosition {
  planetIndex: number;
  /** Where the vehicle stands (a unit surface direction). */
  dir: Vec3;
  kind: 'crest' | 'open';
  /** How far from the objective, in people along the ground. */
  fromObjectivePeople: number;
}

/** The .50's position for a vehicle at `from` covering the objective at `objective` (planet `planetIndex`); null without a planet. */
/**
 * The gunner's eye over the ground on a hull of `type` (people): its gun seat's rim and a head over it (the cupola's 1.43 →
 * GUN_SUPPORT.eyePeople). A pintle seat's rim is its bed's rail, not a ring (63.1o's follow-up): its gunner's eye is where
 * the seat stands it, on the floor behind the gun (core/drive.ts hatchStandPeople), the eye the sim fires from.
 */
export function gunnerEyePeople(type: string): number {
  const seats = (VEHICLE_MODEL_SIZE as Record<string, { seats?: Array<{ role: string; rim?: number; pintle?: number }> }>)[type]?.seats ?? [];
  const index = seats.findIndex(x => x.role === 'weapon'), seat = seats[index];
  const eye = LOS_EYE_HEIGHT / PERSON_BODY_HEIGHT;
  if (seat?.pintle !== undefined) { const stand = hatchStandPeople(type as StructureState['type'], 'weapon', index, eye); if (stand !== null) return stand + eye; }
  return seat?.rim === undefined ? GUN_SUPPORT.eyePeople : seat.rim + (GUN_SUPPORT.eyePeople - 1.43);
}

export function gunSupportPosition(state: GameState, planetIndex: number, objective: Vec3, from: Vec3, eyePeople: number = GUN_SUPPORT.eyePeople): GunSupportPosition | null {
  const planet = state.planets[planetIndex];
  if (!planet) return null;
  const ps = personSetOf(state);
  const person = PERSON_BODY_HEIGHT * ps.k;
  const obj = vecNormalize(objective), at = vecNormalize(from);
  const arcPeople = (a: Vec3, b: Vec3) => (dacos(Math.max(-1, Math.min(1, vecDot(vecNormalize(a), vecNormalize(b))))) * planet.radius) / person;
  const lo = rifleEffectivePeople(), hi = Math.max(lo + 2, GUN_SUPPORT.reach * hmgEffectivePeople()), mid = (lo + hi) / 2;
  const inBand = (d: Vec3) => { const x = arcPeople(d, obj); return x >= lo && x <= hi; };
  const objEye = eyePointAt(planet, obj, ps.losEyeHeight);
  const sees = (d: Vec3) => {
    const eye = eyePointAt(planet, d, eyePeople * person);
    return personLineOfSight(eye, objEye, state) && !segmentCovered(eye, objEye, state, undefined, ps.coverSampleStep);
  };
  const place = (dir: Vec3, kind: GunSupportPosition['kind']): GunSupportPosition => ({ planetIndex, dir, kind, fromObjectivePeople: arcPeople(dir, obj) });

  // 1. a crest in the band that the gunner sees the objective from, the nearest the vehicle
  const crests = terrainSpotsNear(state, planetIndex, obj, hi * person, objEye).filter(t => t.kind === 'crest' && inBand(t.dir) && sees(t.dir));
  const crest = crests.reduce<(typeof crests)[number] | null>((best, t) => (best === null || arcPeople(t.dir, at) < arcPeople(best.dir, at) ? t : best), null);
  if (crest) return place(crest.dir, 'crest');

  // 2. open ground: the band's middle, on the line from the objective toward the vehicle
  const t = vecSub(at, vecScale(obj, vecDot(at, obj)));
  const way = vecLength(t) > 1e-9 ? vecNormalize(t) : vecNormalize(vecSub({ x: 0, y: 1, z: 0 }, vecScale(obj, obj.y)));
  const a = (mid * person) / planet.radius;
  return place(vecNormalize(vecAdd(vecScale(obj, dcos(a)), vecScale(way, dsin(a)))), 'open');
}
